/**
 * MobileBottomNav — Fixed bottom navigation bar for mobile devices.
 *
 * Provides quick access to key sections without opening the drawer.
 * Only visible on screens < 1024px (lg breakpoint).
 * Respects safe area insets for notched phones.
 */

import { NavLink, useLocation, useParams } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Brain,
  Search,
  MoreHorizontal,
} from "lucide-react";
import { useFelixPanelStore } from "@/stores/felixPanel";
import { useSidebarStore } from "@/stores/sidebar";
import { cn } from "@/lib/utils";

export function MobileBottomNav() {
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const felixToggle = useFelixPanelStore((s) => s.toggle);
  const felixOpen = useFelixPanelStore((s) => s.open);
  // Phase 7 (UX v2): Felix tab is the canonical mobile entry point.
  // No flag-gated swap to My Day — one Felix icon per device, always
  // available. My Day is reachable from the More drawer.
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);

  function openCommandPalette() {
    window.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", metaKey: true }),
    );
  }

  const isProjectPage = pathname.startsWith("/projects/") && !!projectId;

  return (
    <nav
      // SITS ABOVE THE COOKIE BANNER, not under it.
      //
      // The banner is `fixed bottom-0 z-50`; this nav was `fixed bottom-0 z-40`.
      // Two fixed elements at the same edge, and the banner won — measured at
      // 390x844 with elementFromPoint, EVERY primary nav target (Home, Projects,
      // Search, Felix, More) returned the banner as the element at its own
      // centre. On a phone, before a consent choice is stored, the whole primary
      // navigation was untappable.
      //
      // The banner publishes its live height as --ftl-cookie-banner-height and
      // the page layout already consumes it as padding-bottom (added when the
      // same banner was found covering the sign-up controls). Padding cannot
      // move a FIXED element, which is why that earlier fix did not reach this
      // one. Offsetting `bottom` by the same variable lifts the nav exactly as
      // far as the banner is tall, and returns it to the edge — the variable
      // falls back to 0px — the moment the banner goes.
      className="fixed bottom-[var(--ftl-cookie-banner-height,0px)] left-0 right-0 z-40 border-t border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur-lg lg:hidden safe-bottom"
      aria-label="Mobile navigation"
    >
      <div className="flex items-stretch">
        {/* Dashboard */}
        <NavLink
          to="/"
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center justify-center min-h-[52px] py-1.5 transition-colors",
              isActive && !isProjectPage
                ? "text-brand-600 dark:text-brand-400"
                : "text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]",
            )
          }
        >
          <LayoutDashboard className="h-5 w-5" />
          <span className="text-[10px] font-medium mt-0.5 truncate max-w-[56px]">Home</span>
        </NavLink>

        {/* Projects */}
        <NavLink
          to="/projects"
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center justify-center min-h-[52px] py-1.5 transition-colors",
              isActive || isProjectPage
                ? "text-brand-600 dark:text-brand-400"
                : "text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]",
            )
          }
        >
          <FolderKanban className="h-5 w-5" />
          <span className="text-[10px] font-medium mt-0.5 truncate max-w-[56px]">Projects</span>
        </NavLink>

        {/* Search */}
        <button
          onClick={openCommandPalette}
          className="flex flex-1 flex-col items-center justify-center min-h-[52px] py-1.5 text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          <Search className="h-5 w-5" />
          <span className="text-[10px] font-medium mt-0.5 truncate max-w-[56px]">Search</span>
        </button>

        {/* Felix — canonical mobile entry point (Phase 7). No flag
            gate; pairs with the Topbar "Ask Felix" pill on desktop. */}
        <button
          onClick={felixToggle}
          aria-label={felixOpen ? "Close Felix" : "Ask Felix"}
          aria-pressed={felixOpen}
          className={cn(
            "flex flex-1 flex-col items-center justify-center min-h-[52px] py-1.5 transition-colors",
            felixOpen
              ? "text-brand-600 dark:text-brand-400"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
          )}
        >
          <Brain className="h-5 w-5" />
          <span className="text-[10px] font-medium mt-0.5 truncate max-w-[56px]">Felix</span>
        </button>

        {/* More (opens drawer) */}
        <button
          onClick={() => setMobileOpen(true)}
          className="flex flex-1 flex-col items-center justify-center min-h-[52px] py-1.5 text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          <MoreHorizontal className="h-5 w-5" />
          <span className="text-[10px] font-medium mt-0.5 truncate max-w-[56px]">More</span>
        </button>
      </div>
    </nav>
  );
}
